import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { UVGeneration } from "geom/uv";

export enum StairConstructionType {
  GENERIC = "GENERIC",
  ASSEMBLED = "ASSEMBLED",
  MONOLITHIC = "MONOLITHIC",
}

export enum StringerPositioning {
  PERCENTAGE = "PERCENTAGE",
  FROM_LEFT = "FROM_LEFT",
  FROM_RIGHT = "FROM_RIGHT",
}

export interface Stringer {
  readonly positioning: StringerPositioning;
  readonly position: number;
  readonly width: number;
  readonly closed: boolean;
}

export interface MonolithicStair extends StairConstructionBase {
  readonly type: StairConstructionType.MONOLITHIC;
  readonly stairBottomShape: StairBottomShape;
  readonly stringerMinDepth: number;
  readonly stairMaterial?: MaterialSrc;
  readonly hasTreadMaterial: boolean;
}

export interface GenericStair extends StairConstructionBase {
  readonly type: StairConstructionType.GENERIC;
  readonly hasTreadMaterial: boolean;
}

export type StairConstruction = MonolithicStair | AssembledStair | GenericStair;

export const defaultConstruction: GenericStair = {
  type: StairConstructionType.GENERIC,
  stairBottomOffset: 0,
  stringerTopExtension: 0,
  hasTreadMaterial: false,
};

export enum StairBottomShape {
  FLAT = "FLAT",
  SLOPED = "SLOPED",
}

export const STAIR_DEFAULT_MATERIAL: MaterialSrc = {
  templateId: 2,
  colorId: 3,
};

export const STAIR_DEFAULT_MATERIAL_OVERRIDE: MaterialSrcOverride = {
  materialSrc: STAIR_DEFAULT_MATERIAL,
  uvGen: UVGeneration.NONE,
};

export enum TreadJustification {
  BOTTOM = "BOTTOM",
  CENTER = "CENTER",
  TOP = "TOP",
}

export interface StairConstructionBase {
  readonly type: StairConstructionType;
  readonly stairBottomOffset: number;
  readonly stringerTopExtension: number;

  // treads
  readonly treadThickness?: number;
  readonly nosing?: number;
  readonly treadMaterial?: MaterialSrc;

  // risers
  readonly hasRisers?: boolean;
  readonly riserThickness?: number;
  readonly riserMaterial?: MaterialSrc;
}

export enum StringerLayout {
  AUTOMATIC = "AUTOMATIC",
  MANUAL = "MANUAL",
}

export interface AssembledStair extends StairConstructionBase {
  readonly type: StairConstructionType.ASSEMBLED;

  // stringers
  readonly stringerMinDepth: number;
  readonly stringerLayout: StringerLayout;
  readonly stringerThickness: number;
  readonly stringerSideOffset: number;
  readonly stringerMaxSpacing: number;
  readonly stringerMaterial?: MaterialSrc;
  /**
   * list of manually-placed stringers
   */
  readonly stringers: Stringer[];
}
